import { InAppReview } from '@capacitor-community/in-app-review';
import { Preferences } from '@capacitor/preferences';
import { logToDatadog } from '@/domains/shared/analytics/datadogUtils';
import { openExternalLink } from '@/domains/shared/utils/openExternalLink';
import {
  getPlatform,
  isAndroid,
  isIOS,
  isNative,
} from '@/domains/shared/utils/native';

/** Preference key: last time we *called* the OS review API (not proof UI was shown). */
const LAST_REVIEW_ATTEMPTED_KEY = 'rate_last_asked_at';
const SESSION_COUNT_KEY = 'rate_session_count';
const SESSION_THRESHOLD = 3;
/**
 * Cooldown between *attempts* to call the OS review API.
 * Kept at 120 days so we do not outpace Apple's StoreKit frequency cap
 * (≈ 3 prompts/year). Neither Apple nor Google report whether the dialog
 * was actually shown; OS quotas still apply on top of this.
 */
const ATTEMPT_COOLDOWN_DAYS = 120;
const WITHIN_ATTEMPT_COOLDOWN_MESSAGE = `within ${ATTEMPT_COOLDOWN_DAYS}-day attempt cooldown`;

/** Delay after Summary is settled before requesting a review (avoids competing with intro / navigation). */
export const REVIEW_PROMPT_SETTLE_MS = 2500;

const IOS_APP_STORE_ID = '1345490587';
const ANDROID_PACKAGE_ID = 'com.pensionbee.mobile.production';

async function incrementSessionCount(): Promise<number> {
  const { value } = await Preferences.get({ key: SESSION_COUNT_KEY });
  const updated = Number(value);

  if (!Number.isFinite(updated)) {
    await Preferences.remove({ key: SESSION_COUNT_KEY });
    return 0;
  }

  const incrementedValue = updated + 1;
  await Preferences.set({
    key: SESSION_COUNT_KEY,
    value: String(incrementedValue),
  });
  return incrementedValue;
}

async function shouldAttemptReview(): Promise<boolean> {
  const { value: lastAttempted } = await Preferences.get({
    key: LAST_REVIEW_ATTEMPTED_KEY,
  });

  if (!lastAttempted) {
    return true;
  }

  const lastAttemptedNumber = Number(lastAttempted);
  if (!Number.isFinite(lastAttemptedNumber)) {
    await Preferences.remove({ key: LAST_REVIEW_ATTEMPTED_KEY });
    return true;
  }

  const daysSince = (Date.now() - lastAttemptedNumber) / (1000 * 60 * 60 * 24);
  return daysSince >= ATTEMPT_COOLDOWN_DAYS;
}

export async function trackAppSession(): Promise<void> {
  try {
    const count = await incrementSessionCount();
    console.info(`[RateApp] App session count: ${count}`);
    logToDatadog('info', 'App session tracked for in-app review', {
      sessionCount: count,
      threshold: SESSION_THRESHOLD,
      meetsThreshold: count >= SESSION_THRESHOLD,
    });
  } catch (error) {
    console.error('[RateApp] Error tracking app session:', error);
    logToDatadog('error', 'trackAppSession failed', {
      error: String(error),
    });
  }
}

/**
 * Asks the OS to present an in-app review dialog if eligible.
 * Resolving does **not** mean the dialog was shown — StoreKit / Play may
 * silently no-op (quota, TestFlight, user settings, etc.).
 */
export async function showReviewIfEligible(): Promise<void> {
  try {
    if (!isNative()) {
      console.info('[RateApp] Skipping review: not on a native platform');
      return;
    }

    const { value } = await Preferences.get({ key: SESSION_COUNT_KEY });
    const count = Number(value);

    if (!Number.isFinite(count)) {
      await Preferences.remove({ key: SESSION_COUNT_KEY });
      return;
    }

    if (count < SESSION_THRESHOLD) {
      console.info(
        `[RateApp] Skipping review: session count ${count} below threshold ${SESSION_THRESHOLD}`,
      );
      logToDatadog('info', 'In-app review skipped: session threshold not met', {
        sessionCount: count,
        threshold: SESSION_THRESHOLD,
      });
      return;
    }

    const canAttempt = await shouldAttemptReview();
    if (!canAttempt) {
      console.info(
        `[RateApp] Skipping review: ${WITHIN_ATTEMPT_COOLDOWN_MESSAGE}`,
      );
      logToDatadog('info', 'In-app review skipped: within attempt cooldown', {
        sessionCount: count,
        cooldownDays: ATTEMPT_COOLDOWN_DAYS,
      });
      return;
    }

    await InAppReview.requestReview();
    // Record the attempt only — the OS does not tell us if UI was displayed.
    await Preferences.set({
      key: LAST_REVIEW_ATTEMPTED_KEY,
      value: String(Date.now()),
    });
    console.info(
      '[RateApp] In-app review requested (OS may suppress the dialog)',
    );
    logToDatadog('info', 'In-app review requested', {
      sessionCount: count,
      platform: getPlatform(),
    });
  } catch (error) {
    console.error('[RateApp] Failed to request in-app review:', error);
    logToDatadog('error', 'In-app review request failed', {
      error: String(error),
    });
  }
}

/**
 * Opens the store listing write-review page. Use for explicit "Rate us"
 * actions — Apple requires a store URL for button-driven review flows
 * because `requestReview` must not be tied to a control.
 */
export function openWriteReviewPage(): void {
  const isIosPlatform = isIOS();
  const isAndroidPlatform = isAndroid();

  if (isIosPlatform) {
    openExternalLink(
      `https://apps.apple.com/app/id${IOS_APP_STORE_ID}?action=write-review`,
    );
    return;
  }

  if (isAndroidPlatform) {
    openExternalLink(
      `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_ID}`,
    );
    return;
  }

  openExternalLink(
    `https://apps.apple.com/app/id${IOS_APP_STORE_ID}?action=write-review`,
  );
}
